import { Check } from "lucide-react";
import { HOME_TYPES, SIZES, STOVES, OCCASIONS, PRICING } from "@/lib/content";
import { zipStatus, visitDates, WINDOWS, fmtDate, dateKey } from "@/lib/booking";
import { usd, DEPOSIT, BASE_PRICE } from "@/lib/addons";
import { ChoiceGroup, Field } from "./Controls";

const INCLUDED = ["Laboratory water testing", "Seven-day bedroom air monitoring", "Gas-cooking NO₂ screening (gas stoves)", "Room-by-room walkthrough", "Written findings + 45-minute review"];

const PackageCard = () => (
  <div className="rounded-md bg-mint p-5 sm:p-6" data-testid="package-card">
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="mono uppercase tracking-[0.16em] text-sec">Your assessment</p>
        <h3 className="mt-1.5 text-xl font-semibold tracking-tight text-forest">Home Hyper Assessment</h3>
        <p className="mt-1.5 text-[15px] leading-relaxed text-ink">A complete air and water picture of your home, explained in plain language.</p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm text-sec line-through decoration-[1px]">${PRICING.future.toLocaleString()}</p>
        <p className="font-serif text-3xl leading-none text-forest" data-testid="package-price">{usd(BASE_PRICE)}</p>
      </div>
    </div>
    <ul className="mt-4 space-y-1.5" data-testid="package-included">
      {INCLUDED.map((t) => (
        <li key={t} className="flex items-start gap-2.5 text-sm text-ink"><span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-forest text-white"><Check size={10} strokeWidth={3} /></span>{t}</li>
      ))}
    </ul>
    <p className="mt-4 border-t border-forest/10 pt-3 text-sm text-ink"><span className="font-semibold">{usd(DEPOSIT)} due today.</span> <span className="text-sec">Remaining {usd(BASE_PRICE - DEPOSIT)} due 24 hours before your visit. Lab fees included.</span></p>
  </div>
);

export const StepAssessment = ({ data, set }) => {
  const status = zipStatus(data.zip);
  const stove = STOVES.find((s) => s.id === data.stove);
  return (
    <div className="space-y-7" data-testid="step-home">
      <PackageCard />
      <p className="text-sm text-sec">Four quick details so we bring the right equipment.</p>
      <div className="max-w-xs">
        <Field id="zip" label="ZIP code" inputMode="numeric" autoComplete="postal-code" maxLength={5} placeholder="10028" value={data.zip} enterKeyHint="next"
          onChange={(e) => set({ zip: e.target.value.replace(/\D/g, "").slice(0, 5) })} data-testid="zip-input" aria-describedby="zip-status" />
        <p id="zip-status" className="mt-2 text-sm leading-relaxed min-h-[1.25rem]" aria-live="polite" data-testid="zip-status">
          {status === "manhattan" && <span className="text-ink font-medium">Manhattan ZIP. We’ll confirm Upper East Side coverage with your appointment.</span>}
          {status === "outside" && <span className="text-[#8A5518] font-medium">Outside our current service area. You can join the waitlist below.</span>}
        </p>
      </div>
      <ChoiceGroup label="Home type" options={HOME_TYPES} value={data.homeType} onChange={(v) => set({ homeType: v })} testId="home-type" />
      <ChoiceGroup label="Approximate size" options={SIZES} value={data.size} onChange={(v) => set({ size: v })} testId="home-size" />
      <ChoiceGroup label="Stove" options={STOVES} value={data.stove} onChange={(v) => set({ stove: v })} testId="stove-type" hint={stove?.note} />
      <ChoiceGroup label="What’s prompting the assessment? (optional)" options={OCCASIONS} value={data.reason} onChange={(v) => set({ reason: data.reason === v ? "" : v })} testId="reason" />
    </div>
  );
};

export const StepAppointment = ({ data, set }) => {
  const dates = visitDates();
  return (
    <div className="space-y-7" data-testid="step-appointment">
      <p className="text-sm text-sec leading-relaxed" data-testid="appointment-note">
        Choose a preferred visit window. We’ll confirm your appointment within 24 hours. All times are Eastern Time.
      </p>
      <fieldset>
        <legend className="label">Preferred date</legend>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2" data-testid="date-options">
          {dates.map((d) => {
            const key = dateKey(d);
            const on = data.date === key;
            return (
              <button key={key} type="button" role="radio" aria-checked={on} onClick={() => set({ date: key, dateLabel: fmtDate(d) })} data-testid={`date-option-${key}`}
                className={`rounded-md border px-1 py-2.5 text-center transition-colors min-h-[76px] ${on ? "border-forest bg-forest text-white shadow-[0_0_0_2px_#163B30]" : "border-sec/60 bg-white text-ink hover:border-forest"}`}>
                <span className={`block mono uppercase ${on ? "text-white/80" : "text-sec"}`}>{fmtDate(d, { weekday: "short" })}</span>
                <span className="block font-serif text-2xl leading-tight mt-0.5">{fmtDate(d, { day: "numeric" })}</span>
                <span className={`block mono ${on ? "text-white/80" : "text-sec"}`}>{fmtDate(d, { month: "short" })}</span>
              </button>
            );
          })}
        </div>
      </fieldset>
      <fieldset>
        <legend className="label">Arrival window</legend>
        <div className="grid grid-cols-3 gap-2" role="radiogroup" data-testid="window">
          {WINDOWS.map((o) => {
            const on = data.window === o.id;
            return (
              <button key={o.id} type="button" role="radio" aria-checked={on} onClick={() => set({ window: o.id })} data-testid={`window-${o.id}`} className={`chip justify-center !px-2 min-h-[52px] ${on ? "chip-active" : "chip-idle"}`}>
                {o.label}
              </button>
            );
          })}
        </div>
      </fieldset>
      {data.date && data.window && (
        <p className="rounded-md bg-mint px-4 py-3 text-sm text-ink" data-testid="appointment-selected">
          <span className="font-semibold">{data.dateLabel}</span> · {WINDOWS.find((w) => w.id === data.window)?.label} arrival
        </p>
      )}
    </div>
  );
};

export const StepDetails = ({ data, set, errors = {}, touch }) => {
  const f = (id, label, extra = {}) => (
    <Field id={id} label={label} value={data[id]} onChange={(e) => set({ [id]: e.target.value })} onBlur={() => touch(id)} error={errors[id]} data-testid={`${id}-input`} enterKeyHint="next" {...extra} />
  );
  return (
    <div className="space-y-5" data-testid="step-details">
      {f("name", "Full name", { autoComplete: "name", autoCapitalize: "words" })}
      {f("email", "Email", { type: "email", autoComplete: "email", inputMode: "email", autoCapitalize: "none" })}
      {f("phone", "Phone", { type: "tel", autoComplete: "tel", inputMode: "tel", placeholder: "(212) 555-0142" })}
      {f("address", "Street address", { autoComplete: "street-address" })}
      {f("apt", "Apartment / unit", { optional: true, autoComplete: "address-line2" })}
      <input type="text" name="website" value={data.website} onChange={(e) => set({ website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div>
        <label htmlFor="access" className="label">Building access notes <span className="font-normal text-sec">(optional)</span></label>
        <textarea id="access" rows={2} className="field min-h-[72px] sm:min-h-[96px] resize-y" placeholder="Doorman, buzzer, elevator, parking…" value={data.access} onChange={(e) => set({ access: e.target.value })} data-testid="access-input" enterKeyHint="done" />
      </div>
    </div>
  );
};
